import { Outlet, Link } from "react-router";
import FilterProvider from "@/context/filter";
import { useAuth } from "@/context/auth";
import { useMemberTemplate } from "@/hooks/queries/useDashboardData";
import { Button, buttonVariants } from "@/components/ui/Button";
import { cn } from "@/util/misc";
import AvatarCollapsibleMenu from "@/components/ui/AvatarCollapsibleMenu";
import { DynamicBreadcrumbs } from "@/components/features/dashboard/DynamicBreadcrumbs";
import { GetStarted } from "@/components/features/dashboard/GetStarted";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/Collapsible";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarRail,
  SidebarTrigger,
} from "@/components/ui/Sidebar";
import { Separator } from "@/components/ui/Separator";
import {
  DollarSign,
  FileText,
  Users,
  Briefcase,
  Wrench,
  Settings,
  LayoutDashboard,
  Upload,
  ClipboardMinus,
  Send,
  ChevronRight,
  Activity,
} from "lucide-react";

interface SidebarItemDef {
  title: string;
  href: string;
  icon?: React.ElementType;
  adminOnly?: boolean;
}

interface SidebarGroupDef {
  id: string;
  title: string;
  icon?: React.ElementType;
  items: SidebarItemDef[];
  defaultOpen?: boolean;
  adminOnly?: boolean;
}

const sidebarNavigation: SidebarGroupDef[] = [
  {
    id: "core-operations",
    title: "Core Operations",
    icon: FileText,
    defaultOpen: true,
    items: [
      {
        title: "Expected Payments",
        href: "/dashboard/agent-expected-payment",
        icon: FileText,
      },
      {
        title: "Sold Contract Upload",
        href: "/dashboard/sold-contract-upload",
        icon: Upload,
      },
      {
        title: "Carrier Payments",
        href: "/dashboard/file-upload",
        icon: DollarSign,
        adminOnly: true,
      },
    ],
  },
  {
    id: "management",
    title: "Management",
    icon: Users,
    defaultOpen: true,
    adminOnly: true,
    items: [
      {
        title: "Reports & Summary",
        href: "/dashboard/reports",
        icon: ClipboardMinus,
      },
      { title: "Send Report", href: "/dashboard/send-report", icon: Send },
      {
        title: "Agencies & Agents",
        href: "/dashboard/agencies-and-agents",
        icon: Users,
      },
      { title: "Carriers", href: "/dashboard/carriers", icon: Briefcase },
      { title: "Agency Overrides", href: "/dashboard/override", icon: Wrench },
      {
        title: "Activity Log",
        href: "/dashboard/activity-log",
        icon: Activity,
      },
    ],
  },
];

export default function DashboardLayout() {
  const { user } = useAuth();
  const { data: memberTemplateData } = useMemberTemplate();
  const isAdmin = user?.isAdmin ?? false;
  const isMemberTemplateDefined = memberTemplateData?.[0] ?? true;

  const filteredNavigation = sidebarNavigation.reduce<SidebarGroupDef[]>(
    (acc, group) => {
      if (group.adminOnly && !isAdmin) return acc;
      const filteredItems = group.items.filter(
        (item) => !(item.adminOnly && !isAdmin),
      );
      if (filteredItems.length > 0) {
        acc.push({ ...group, items: filteredItems });
      }
      return acc;
    },
    [],
  );

  return (
    <section className="p-0">
      {!isMemberTemplateDefined && isAdmin && (
        <div className="fixed left-0 top-0 z-50 flex h-full w-full bg-black/50 p-6 backdrop-blur-sm">
          <div className="h-full w-1/2 rounded-l-lg bg-white shadow-lg">
            <GetStarted />
          </div>
          <div className="relative h-full w-1/2">
            <div
              id="portal-columns"
              className="absolute left-0 top-0 z-10 h-full w-full"
            ></div>
            <img
              src="/get-started.jpg"
              alt="Office people"
              className="h-full w-full rounded-r-lg object-cover"
            />
          </div>
        </div>
      )}

      <div className="flex min-h-screen">
        <SidebarProvider>
          <Sidebar className={cn("z-40 w-[247px] flex-shrink-0")}>
            <SidebarHeader>
              <h1 className="self-center text-xl font-bold tracking-wide align-middle justify-center">
                ROCA
              </h1>
            </SidebarHeader>
            <SidebarContent className="flex flex-col gap-2 p-2">
              <SidebarMenuItem className="mb-2">
                <Button
                  asChild
                  variant="outline"
                  className={cn(
                    "w-full justify-start font-semibold text-lg py-3 px-4",
                    buttonVariants({ variant: "ghost" }),
                  )}
                >
                  <Link to="/dashboard">
                    <LayoutDashboard className="mr-3 h-5 w-5" /> Dashboard Home
                  </Link>
                </Button>
              </SidebarMenuItem>

              {isAdmin && (
                <SidebarMenuItem className="mb-4">
                  <Button
                    asChild
                    className={cn(
                      "w-full justify-start font-semibold text-lg py-3 px-4",
                      buttonVariants({ variant: "default" }),
                    )}
                  >
                    <Link to="/dashboard/payment-tracking">
                      <DollarSign className="mr-3 h-5 w-5" />
                      Payment Tracker
                    </Link>
                  </Button>
                </SidebarMenuItem>
              )}

              {filteredNavigation.map((group) => (
                <Collapsible
                  key={group.id}
                  defaultOpen={group.defaultOpen}
                  className="group/collapsible"
                >
                  <SidebarGroup>
                    <SidebarGroupLabel asChild>
                      <CollapsibleTrigger
                        className={cn(
                          "flex w-full items-center justify-between rounded-md px-3 py-2 text-sm font-medium text-sidebar-foreground",
                          "transition-colors duration-200 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
                        )}
                      >
                        <div className="flex items-center">
                          {group.icon && (
                            <group.icon className="mr-2 h-4 w-4" />
                          )}
                          {group.title}
                        </div>
                        <ChevronRight className="h-4 w-4 shrink-0 transition-transform group-data-[state=open]/collapsible:rotate-90" />
                      </CollapsibleTrigger>
                    </SidebarGroupLabel>
                    <CollapsibleContent>
                      <SidebarGroupContent className="py-1">
                        <SidebarMenu>
                          {group.items.map((item) => (
                            <SidebarMenuItem key={item.href}>
                              <SidebarMenuButton asChild>
                                <Link
                                  to={item.href}
                                  className={cn(
                                    "flex items-center rounded-md px-4 py-2 text-sm font-medium",
                                    "transition-colors duration-200 hover:bg-accent hover:text-accent-foreground",
                                  )}
                                >
                                  {item.icon && (
                                    <item.icon className="mr-3 h-4 w-4" />
                                  )}
                                  {item.title}
                                </Link>
                              </SidebarMenuButton>
                            </SidebarMenuItem>
                          ))}
                        </SidebarMenu>
                      </SidebarGroupContent>
                    </CollapsibleContent>
                  </SidebarGroup>
                </Collapsible>
              ))}
            </SidebarContent>
            <SidebarFooter>
              <Link
                to="/dashboard/settings"
                className="pointer text-xs text-gray-800"
              >
                <Settings />
              </Link>
            </SidebarFooter>
            <SidebarRail />
          </Sidebar>

          <SidebarInset className="min-w-0 overflow-x-hidden">
            <header className="sticky top-0 z-10 flex shrink-0 items-center gap-2 border-b bg-white px-4 dark:bg-neutral-950">
              <div className="flex items-center gap-2 p-4">
                <SidebarTrigger className="-ml-1" />
                <Separator orientation="vertical" className="mr-2 h-4" />
                <DynamicBreadcrumbs />
              </div>
              <div className="ml-auto flex items-center gap-2">
                <h1 className="text-black">
                  Welcome,{" "}
                  <span className="capitalize font-bold">{user?.name}</span>
                </h1>
                <AvatarCollapsibleMenu />
              </div>
            </header>
            <div className="min-w-0 flex-1 overflow-x-hidden p-4">
              <FilterProvider>
                <Outlet />
              </FilterProvider>
            </div>
          </SidebarInset>
        </SidebarProvider>
      </div>
    </section>
  );
}
